import { useEffect, useMemo, useRef, type ReactNode } from "react";

import {
  useVisitFormContext,
  VisitForm,
} from "~/screens/Scheduling/PersonalInformation/components/VisitForm/visit-form";
import { FORM_AUDIENCE } from "~/screens/Scheduling/PersonalInformation/components/VisitForm/visit-form-sections";
import { VISIT_TYPES } from "~/services/scheduling/constants";
import type { VisitType } from "~/services/scheduling/types";
import { Button } from "~/shared/components/ui/Button/Button";
import {
  buildStaffPatientInfoSchema,
  type StaffPatientInfoFormData,
} from "../utils/staff-booking-schemas";
import { FollowUpAddressSection } from "./follow-up-address-section";
import { StaffBookingFooter } from "./staff-booking-footer";

export type StaffPatientInfoFieldErrors = Partial<
  Record<keyof StaffPatientInfoFormData, string>
>;

type PatientInfoFormProps = {
  visitType: VisitType;
  hasEhrId: boolean;
  hasPlan: boolean;
  stateId: number;
  defaultValues?: Partial<StaffPatientInfoFormData>;
  fieldErrors: StaffPatientInfoFieldErrors;
  hasFileSizeError: boolean;
  isLoading: boolean;
  errorBanner?: (onChooseAnotherSlot: () => void) => ReactNode;
  onSubmit: (data: StaffPatientInfoFormData) => void;
  onBack: (values: Partial<StaffPatientInfoFormData>) => void;
  onChooseAnotherSlot: (values: Partial<StaffPatientInfoFormData>) => void;
  onHiddenFieldErrors: (messages: string[]) => void;
};

const PatientInfoActions = ({
  defaultValues,
  fieldErrors,
  isLoading,
  errorBanner,
  onBack,
  onChooseAnotherSlot,
  onHiddenFieldErrors,
}: Pick<
  PatientInfoFormProps,
  | "defaultValues"
  | "fieldErrors"
  | "isLoading"
  | "errorBanner"
  | "onBack"
  | "onChooseAnotherSlot"
  | "onHiddenFieldErrors"
>) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const {
    form,
    fieldErrorVisibility,
    setInsuranceCardFront,
    setInsuranceCardBack,
  } = useVisitFormContext();

  useEffect(() => {
    if (defaultValues?.insuranceCardFront) {
      setInsuranceCardFront(defaultValues.insuranceCardFront);
    }
    if (defaultValues?.insuranceCardBack) {
      setInsuranceCardBack(defaultValues.insuranceCardBack);
    }
  }, []);

  useEffect(() => {
    Object.entries(fieldErrors).forEach(([field, message]) => {
      form.setError(field, { type: "server", message });
      fieldErrorVisibility.showError(field);
    });

    if (!Object.keys(fieldErrors).length) return;

    const formElement = anchorRef.current?.closest("form");
    const hiddenFieldMessages = Object.entries(fieldErrors)
      .filter(
        ([field]) =>
          !formElement?.querySelector(`[name="${field}"], [name^="${field}-"]`),
      )
      .map(([, message]) => message);

    if (hiddenFieldMessages.length) onHiddenFieldErrors(hiddenFieldMessages);

    requestAnimationFrame(() =>
      anchorRef.current
        ?.closest("form")
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus(),
    );
  }, [fieldErrors]);

  const getValues = (): Partial<StaffPatientInfoFormData> => form.getValues();

  return (
    <>
      <span ref={anchorRef} hidden />
      {errorBanner?.(() => onChooseAnotherSlot(getValues()))}
      <StaffBookingFooter
        onBack={() => onBack(getValues())}
        backDisabled={isLoading}
        continueProps={{
          type: "submit",
          isLoading,
          disabled: isLoading,
        }}
        continueLabel="Confirm visit"
        sticky={false}
      />
    </>
  );
};

export const PatientInfoForm = ({
  visitType,
  hasEhrId,
  hasPlan,
  stateId,
  defaultValues,
  hasFileSizeError,
  onSubmit,
  ...actionsProps
}: PatientInfoFormProps) => {
  const schema = useMemo(
    () => buildStaffPatientInfoSchema({ visitType, hasEhrId, hasPlan }),
    [visitType, hasEhrId, hasPlan],
  );
  const isFollowUp = visitType === VISIT_TYPES.FOLLOW_UP;
  const showPatientDetails = !isFollowUp && !hasEhrId;
  const insuranceOptions = {
    hasPlan,
    hasFileSizeError,
    cardsRequired: false,
    fieldsRequired: !isFollowUp,
    showGroupNumber: true,
  };

  return (
    <VisitForm
      schema={schema}
      onSubmit={onSubmit}
      isLoading={actionsProps.isLoading}
      fillInsuranceLater={visitType === VISIT_TYPES.INITIAL}
      initialValues={defaultValues}
    >
      {showPatientDetails && <VisitForm.GeneralInfo />}
      {showPatientDetails && <VisitForm.ContactInfo />}
      {isFollowUp ? (
        <FollowUpAddressSection />
      ) : (
        <VisitForm.Address
          isRequired={false}
          isZipCodeRequired={visitType !== VISIT_TYPES.COACH}
        />
      )}
      {visitType === VISIT_TYPES.INITIAL && (
        <VisitForm.OptionalInsurance
          label="Would you like to add insurance now or later?*"
          showLaterWarning={false}
          insuranceOptions={insuranceOptions}
        />
      )}
      {isFollowUp && (
        <VisitForm.OptionalInsurance
          switchVariant="secondary"
          label="Has the patient's insurance changed since their last visit?*"
          insuranceOptions={insuranceOptions}
        />
      )}
      {showPatientDetails && <VisitForm.ReferralSource />}
      <VisitForm.BookingNotes />
      <VisitForm.CancellationPolicy audience={FORM_AUDIENCE.STAFF} />
      <VisitForm.StateDisclaimer
        stateId={stateId}
        audience={FORM_AUDIENCE.STAFF}
      />
      <PatientInfoActions defaultValues={defaultValues} {...actionsProps} />
    </VisitForm>
  );
};

export const ChooseAnotherSlotButton = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="mt-2 w-fit"
    onClick={onClick}
  >
    Choose another slot
  </Button>
);
